// src/features/tag/components/DeleteTagModal.tsx

import { useEffect, useState } from 'react';
import { Alert, Anchor, Button, Group, List, Modal, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconPlaylist } from '@tabler/icons-react';
import { TagManageUtils, type TagRulePlaylistRef } from '../TagManageUtils';
import type { TagUsage } from '@/lib/types/Tag';

const MAX_PLAYLISTS_LISTED = 6;

interface DeleteTagModalProps {
  usage: TagUsage | null;
  playlistRefs: TagRulePlaylistRef[];
  isPending: boolean;
  onClose: () => void;
  onDelete: (tagId: number) => void;
  /** Navigates to the videos grid filtered to this tag (closes the surface). */
  onViewVideos: (label: string) => void;
  /** Navigates to the streams grid searched for this tag (closes the surface). */
  onViewStreams: (label: string) => void;
}

/**
 * Cascade-delete confirmation for an in-use tag: spells out exactly what is
 * removed, links to the affected content, warns about smart playlists, and
 * requires typing the tag name for large cascades. (Unused tags never get
 * here — they use a light confirm.)
 */
export function DeleteTagModal({
  usage,
  playlistRefs,
  isPending,
  onClose,
  onDelete,
  onViewVideos,
  onViewStreams,
}: DeleteTagModalProps) {
  const [typedName, setTypedName] = useState('');

  useEffect(() => {
    setTypedName('');
  }, [usage]);

  if (usage === null) {
    return null;
  }

  const needsTyped = TagManageUtils.needsTypedConfirm(usage);
  const typedOk = !needsTyped || typedName.trim() === usage.label;
  const listedPlaylists = playlistRefs.slice(0, MAX_PLAYLISTS_LISTED);

  return (
    <Modal opened onClose={onClose} title="Delete Tag" size="md">
      <Stack gap="md">
        <Text size="sm">
          Deleting{' '}
          <Text span fw={600}>
            &ldquo;{usage.label}&rdquo;
          </Text>{' '}
          removes it from {TagManageUtils.formatUsage(usage)}. This cannot be undone.
        </Text>

        <Group gap="md">
          {usage.videoCount > 0 && (
            <Anchor size="sm" onClick={() => onViewVideos(usage.label)}>
              View affected videos
            </Anchor>
          )}
          {usage.streamCount > 0 && (
            <Anchor size="sm" onClick={() => onViewStreams(usage.label)}>
              View affected streams
            </Anchor>
          )}
        </Group>

        {(usage.scheduleCount > 0 || usage.ingestCfgCount > 0) && (
          <Text size="sm" c="dimmed">
            Stream record schedules and upload configs that apply this tag will stop
            adding it to future recordings and uploads.
          </Text>
        )}

        {playlistRefs.length > 0 && (
          <Alert icon={<IconPlaylist size={16} />} color="yellow" title="Smart Playlists use this tag">
            <Stack gap={4}>
              <List size="sm" spacing={2}>
                {listedPlaylists.map((p) => (
                  <List.Item key={p.listId}>{p.title}</List.Item>
                ))}
              </List>
              {playlistRefs.length > MAX_PLAYLISTS_LISTED && (
                <Text size="sm">and {playlistRefs.length - MAX_PLAYLISTS_LISTED} more</Text>
              )}
              <Text size="sm">
                The tag rule is removed from these playlists, which can change
                which content they include on your site and players.
              </Text>
            </Stack>
          </Alert>
        )}

        {needsTyped && (
          <TextInput
            label={`Type the tag name to confirm (${usage.label})`}
            placeholder={usage.label}
            value={typedName}
            onChange={(e) => setTypedName(e.currentTarget.value)}
            leftSection={<IconAlertTriangle size={16} />}
          />
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button color="red" loading={isPending} disabled={!typedOk} onClick={() => onDelete(usage.tagId)}>
            Delete Tag
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
